import type { AppRole } from "@/contexts/AuthContext";

/**
 * Single source of truth for role-based routing.
 *
 * Adjust `home`, `homeLabel`, or `allowedPrefixes` here to change where
 * each role lands and which subroutes they may access. All helpers
 * (`homeForRole`, `homeLabelForRole`, `canRoleAccess`) are derived from
 * this config — no logic edits needed.
 *
 * `publicPaths` are accessible to any role (including signed-out users).
 *
 * Exported for tests so behavior can be asserted directly against the
 * config without re-implementing it.
 */
export interface RoleRouteConfig {
  /** Paths anyone can land on, regardless of role. Exact match. */
  publicPaths: readonly string[];
  /** Where signed-out users go (also their "home" target). */
  signedOutHome: string;
  /** Where signed-out users see as the CTA label. */
  signedOutHomeLabel: string;
  /** Per-role landing page, label, and allowed route prefixes. */
  roles: Record<
    AppRole,
    {
      home: string;
      homeLabel: string;
      /**
       * Path prefixes this role may access. A path matches if it equals
       * a prefix or starts with `prefix + "/"` (no lookalike matches).
       */
      allowedPrefixes: readonly string[];
    }
  >;
}

export const ROLE_ROUTE_CONFIG: RoleRouteConfig = {
  publicPaths: ["/", "/auth", "/signup-client", "/.lovable/oauth/consent"],
  signedOutHome: "/auth",
  signedOutHomeLabel: "Log in",
  roles: {
    admin: {
      // Admins land on the client dashboard by default. They can
      // still navigate to /admin manually when needed.
      home: "/app",
      homeLabel: "Go to my dashboard",
      // Admin is a super-user: can reach any authed area.
      allowedPrefixes: ["/admin", "/coach", "/app", "/affiliate", "/portal", "/my-account", "/welcome"],
    },
    coach: {
      home: "/coach",
      homeLabel: "Go to coach dashboard",
      // Coaches can also train as athletes, so their own athlete area is open.
      allowedPrefixes: ["/coach", "/affiliate", "/app", "/portal", "/my-account", "/welcome"],
    },
    client: {
      home: "/app",
      homeLabel: "Go to my dashboard",
      allowedPrefixes: ["/app", "/affiliate", "/portal", "/my-account", "/welcome"],
    },
  },
};

/**
 * Default landing path for a given role. Used by route guards, the 403
 * page, the role-error page, and post-auth redirects so every "go home"
 * link in the app stays in sync.
 */
export function homeForRole(role: AppRole | null): string {
  if (!role) return ROLE_ROUTE_CONFIG.signedOutHome;
  return ROLE_ROUTE_CONFIG.roles[role].home;
}

/** Human label for the home destination — handy for buttons & toasts. */
export function homeLabelForRole(role: AppRole | null): string {
  if (!role) return ROLE_ROUTE_CONFIG.signedOutHomeLabel;
  return ROLE_ROUTE_CONFIG.roles[role].homeLabel;
}

/**
 * Strict role-access check.
 *
 * Rules:
 *  - Path MUST start with `/` and not be protocol-relative (`//…`) or
 *    backslash-prefixed (browsers may normalize `\` to `/`).
 *  - Reject control characters (NUL, CR/LF, TAB).
 *  - Reject malformed percent-encoding (decodeURIComponent throws).
 *  - Reject encoded slashes/dots (`%2F`, `%5C`, `%2E`) — common smuggling
 *    vectors that could sneak past the prefix check.
 *  - Reject path-traversal segments (`.`, `..`) anywhere in the pathname.
 *  - Reject empty segments (e.g. `/app//x`).
 *  - Public paths (config.publicPaths) are always allowed.
 *  - Otherwise the pathname (querystring/hash stripped) must start with
 *    one of the role's allowed prefixes followed by `/` or end-of-string.
 */
export function canRoleAccess(role: AppRole | null, path: string): boolean {
  if (!path || typeof path !== "string") return false;

  if (!path.startsWith("/")) return false;
  if (path.startsWith("//") || path.startsWith("/\\") || path.startsWith("\\"))
    return false;

  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(path)) return false;

  try {
    decodeURIComponent(path);
  } catch {
    return false;
  }

  if (/%2f|%5c|%2e/i.test(path)) return false;

  const pathname = path.split(/[?#]/, 1)[0];

  const segments = pathname.split("/");
  if (segments.some((seg) => seg === "." || seg === "..")) return false;
  // Reject empty internal segments (`/app//x`), but allow `/` itself
  // which splits into ["", ""].
  if (pathname !== "/" && segments.slice(1).some((seg) => seg === ""))
    return false;

  if (ROLE_ROUTE_CONFIG.publicPaths.includes(pathname)) return true;

  if (!role) return false;

  const allowed = ROLE_ROUTE_CONFIG.roles[role].allowedPrefixes;
  return allowed.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
}
